import { sanitizeAuthoredHtml } from '@low6-pickem/frontend-shared';
import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, X } from 'lucide-react';
import Header from '../components/layout/Header';
import { useGetContestRulesQuery } from '../api/squares/getContestRules';
import {
  fixTermsLinksInHtml,
  handleTermsClick,
  SQUARES_TERMS_OF_SERVICE_URL,
} from '../utils/squaresTermsLink';
import {
  APP_COLUMN_BG,
  APP_GUTTER_BG,
  DESKTOP_COLUMN,
  DESKTOP_COLUMN_FRAME,
  READABLE_CONTENT,
} from '../utils/desktopColumn';
import { appendLobbyParams } from '../utils/lobbyNavigation';

/**
 * Help View
 * Shows "HOW TO PLAY AND RULES" information
 * Fetches actual rules from the backend based on the contest's game rules version
 */
export default function HelpView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const contestId = searchParams.get('contestId');

  // Fetch the rules for this contest
  const { data: rules, isLoading, error } = useGetContestRulesQuery(contestId);

  const rulesText = rules?.rules_text;
  const isHtmlRules = Boolean(rulesText?.trim().startsWith('<'));

  const fixedHtml = useMemo(
    () => (rulesText && isHtmlRules ? fixTermsLinksInHtml(rulesText) : null),
    [rulesText, isHtmlRules]
  );

  const goToTerms = (e: React.MouseEvent) => {
    e.preventDefault();
    window.location.href = SQUARES_TERMS_OF_SERVICE_URL;
  };

  const handleClose = () => {
    const returnTo = searchParams.get('returnTo');
    const rulesVariant = searchParams.get('rulesVariant');

    // Opened from RulesView
    if (returnTo === 'rules') {
      // Rules popup lives inside PlayView; reopen it explicitly.
      if (rulesVariant === 'popup' && contestId) {
        const params = new URLSearchParams();
        params.set('contestId', contestId);
        const modeFromRules = searchParams.get('mode');
        if (modeFromRules) {
          params.set('mode', modeFromRules);
        }
        navigate(appendLobbyParams(`/play?${params.toString()}`, searchParams), {
          state: { openRulesPopup: true },
        });
        return;
      }

      // Standalone/entry rules can safely return via history.
      navigate(-1);
      return;
    }

    // Navigate back to the play view with the same contestId and mode (if provided)
    const mode = searchParams.get('mode');

    if (contestId) {
      // Navigate back to the specific contest grid
      const params = new URLSearchParams();
      params.set('contestId', contestId);
      if (mode) {
        params.set('mode', mode);
      }
      navigate(appendLobbyParams(`/play?${params.toString()}`, searchParams));
    } else {
      // Fallback: navigate to generic play view if no contestId
      navigate(appendLobbyParams('/play', searchParams));
    }
  };

  // Split rules text into paragraphs (by double newline or single newline)
  const renderRulesText = (text: string) => {
    // First try to split by double newlines (paragraphs)
    const paragraphs = text.split(/\n\n+/).filter(p => p.trim());

    if (paragraphs.length > 1) {
      return paragraphs.map((paragraph, index) => (
        <p
          key={index}
          className="text-white"
          style={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
          }}
        >
          {paragraph.split('\n').map((line, lineIndex) => (
            <span key={lineIndex}>
              {line}
              {lineIndex < paragraph.split('\n').length - 1 && <br />}
            </span>
          ))}
        </p>
      ));
    }

    // If only single newlines, split by single newline
    const lines = text.split('\n').filter(l => l.trim());
    return lines.map((line, index) => (
      <p
        key={index}
        className="text-white"
        style={{
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '20px',
        }}
      >
        {line}
      </p>
    ));
  };

  return (
    <div className={`min-h-screen ${APP_GUTTER_BG} flex flex-col`}>
      {/* Desktop container wrapper — width comes from DESKTOP_COLUMN */}
      <div
        className={`w-full ${APP_COLUMN_BG} ${DESKTOP_COLUMN} ${DESKTOP_COLUMN_FRAME} flex flex-col flex-1`}
      >
        {/* Header (X button removed) */}
        <Header onClose={handleClose} />

        {/* Content */}
        <div className={`flex-1 px-4 py-6 relative ${READABLE_CONTENT}`}>
          {/* Title row with close button */}
          <div className="flex items-start justify-between gap-4 mb-6">
            <h1
              className="text-white text-left flex-1"
              style={{
                fontFamily: '"Saira Extra Condensed", sans-serif',
                fontWeight: 800,
                fontSize: '24px',
                lineHeight: '28px',
                letterSpacing: '0.5px',
              }}
            >
              HOW TO PLAY AND RULES
            </h1>
            <button
              type="button"
              onClick={handleClose}
              className="shrink-0 p-1.5 rounded-full text-zinc-400 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-[#53d337] focus:ring-offset-2 focus:ring-offset-[#121212] transition-colors"
              aria-label="Close help"
            >
              <X className="h-6 w-6" strokeWidth={2} />
            </button>
          </div>

          {/* Rules Content */}
          <div className="space-y-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-[#53d337]" />
              </div>
            ) : error ? (
              <p
                className="text-red-400"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                Failed to load rules. Please try again later.
              </p>
            ) : rulesText ? (
              isHtmlRules && fixedHtml != null ? (
                <div
                  className="text-white prose prose-invert prose-sm max-w-none"
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    lineHeight: '20px',
                  }}
                  onClickCapture={handleTermsClick}
                  dangerouslySetInnerHTML={{ __html: sanitizeAuthoredHtml(fixedHtml) }}
                />
              ) : (
                <>
                  {renderRulesText(rulesText)}
                  <p className="mt-6 text-zinc-500">
                    <a
                      href={SQUARES_TERMS_OF_SERVICE_URL}
                      onClick={goToTerms}
                      className="text-[#53d337] underline hover:opacity-90"
                    >
                      Terms of Service
                    </a>
                  </p>
                </>
              )
            ) : (
              <div
                className="space-y-4"
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                }}
              >
                <p className="text-zinc-400">No rules have been configured for this contest yet.</p>
                <p className="text-zinc-400">
                  <a
                    href={SQUARES_TERMS_OF_SERVICE_URL}
                    onClick={goToTerms}
                    className="text-[#53d337] underline hover:opacity-90"
                  >
                    Terms of Service
                  </a>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
